import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';

export default function FittableImage({
  element,
  updateElement,
  ElementControls,
  isInZone = false,
  layoutId = null,
  zoneId = null,
  onElementClick = null
}) {
  // Make frame responsive - handle both pixel values and percentage
  const getFrameWidth = (styleWidth) => {
    if (styleWidth === "100%" || styleWidth === "auto") {
      return "100%"; // Let CSS handle responsive width
    }
    if (typeof styleWidth === 'number') return styleWidth;
    if (typeof styleWidth === 'string') {
      const numValue = parseInt(styleWidth, 10);
      return isNaN(numValue) ? 400 : numValue;
    }
    return 400;
  };

  const getFrameHeight = (styleHeight, frameWidth) => {
    if (styleHeight === "auto" || styleHeight === "100%") {
      // For responsive frames, use a reasonable aspect ratio
      return frameWidth === "100%" ? 300 : 300;
    }
    if (typeof styleHeight === 'number') return styleHeight;
    if (typeof styleHeight === 'string') {
      const numValue = parseInt(styleHeight, 10);
      return isNaN(numValue) ? 300 : numValue;
    }
    return 300;
  };

  const baseFrameWidth = getFrameWidth(element?.styles?.width);
  const baseFrameHeight = getFrameHeight(element?.styles?.height, baseFrameWidth);
  const isResponsiveWidth = baseFrameWidth === "100%";

  const [imageSrc, setImageSrc] = useState(element?.imageSrc || '');
  const [imagePosition, setImagePosition] = useState(element?.imagePosition || { x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(element?.imageScale || 1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = useState(!element?.imageSrc);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Resize functionality
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartData, setResizeStartData] = useState(null);
  const [currentFrameHeight, setCurrentFrameHeight] = useState(() => {
    // Initialize with element height or default height
    if (element?.styles?.height) {
      if (typeof element.styles.height === 'number') return element.styles.height;
      if (typeof element.styles.height === 'string') {
        const numValue = parseInt(element.styles.height, 10);
        return isNaN(numValue) ? 300 : numValue;
      }
    }
    return baseFrameHeight;
  });

  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const currentImagePos = useRef(imagePosition);

  // Preset frame dimensions
  const getPresetDimensions = (preset) => {
    const baseWidth = isResponsiveWidth ? 400 : (typeof baseFrameWidth === 'number' ? baseFrameWidth : 400);

    switch (preset) {
      case 'square':
        return { width: baseWidth, height: baseWidth };
      case '16:9':
        return { width: baseWidth, height: Math.round(baseWidth * 9 / 16) };
      case '9:16':
        return { width: baseWidth, height: Math.round(baseWidth * 16 / 9) };
      default:
        return { width: baseWidth, height: baseFrameHeight };
    }
  };

  const handlePresetChange = (preset) => {
    console.log('🎨 FittableImage preset change:', { preset, elementId: element.id, currentImageSrc: imageSrc, elementImageSrc: element.imageSrc });
    const dimensions = getPresetDimensions(preset);
    const newHeight = dimensions.height;

    setCurrentFrameHeight(newHeight);

    const updates = {
      styles: {
        ...element.styles,
        width: isResponsiveWidth ? "100%" : dimensions.width,
        height: newHeight
      }
    };

    console.log('🔄 Calling updateElement with:', { updates, isInZone, layoutId, zoneId, preservedImageSrc: element.imageSrc });

    // Update element with new dimensions
    updateElement(element.id, updates, isInZone, layoutId, zoneId);
  };

  // Update state when element prop changes
  useEffect(() => {
    console.log('📥 FittableImage element changed:', {
      hasImageSrc: !!element?.imageSrc,
      imageSrc: element?.imageSrc?.substring(0, 50),
      hasPosition: !!element?.imagePosition,
      hasScale: element?.imageScale !== undefined,
      currentImageSrc: imageSrc?.substring(0, 50)
    });

    if (element?.imageSrc) {
      setImageSrc(element.imageSrc);
      setIsEditing(false);
    }
    if (element?.imagePosition) {
      setImagePosition(element.imagePosition);
    }
    if (element?.imageScale !== undefined) {
      setImageScale(element.imageScale);
    }
  }, [element]);

  // Update refs when position changes
  useEffect(() => {
    currentImagePos.current = imagePosition;
  }, [imagePosition]);

  // Update local frame height when element changes (but not during resize)
  useEffect(() => {
    if (element?.styles?.height && !isResizing) {
      const heightValue = typeof element.styles.height === 'number'
        ? element.styles.height
        : parseInt(element.styles.height, 10) || baseFrameHeight;
      setCurrentFrameHeight(heightValue);
    }
  }, [element?.styles?.height, isResizing, baseFrameHeight]);

  // Load image dimensions when imageSrc changes
  useEffect(() => {
    if (imageSrc) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = imageSrc;
    }
  }, [imageSrc]);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
        setIsEditing(false);
        setImagePosition({ x: 0, y: 0 }); // Use 0,0 but it will be centered by CSS

        // Load image to get dimensions and set appropriate scale
        const img = new Image();
        img.onload = () => {
          const imgWidth = img.naturalWidth;
          const imgHeight = img.naturalHeight;

          setImageDimensions({ width: imgWidth, height: imgHeight });

          // Calculate scale to fit frame height
          const frameHeight = baseFrameHeight;
          const frameWidth = isResponsiveWidth ? 400 : (typeof baseFrameWidth === 'number' ? baseFrameWidth : 400);

          const scaleForHeight = frameHeight / imgHeight;
          const scaleForWidth = frameWidth / imgWidth;

          // Use the smaller scale to fit within frame bounds
          const initialScale = Math.min(scaleForHeight, scaleForWidth, 1);

          setImageScale(initialScale);

          // Update the element with the new image and calculated scale
          updateElement(element.id, {
            imageSrc: e.target.result,
            imagePosition: { x: 0, y: 0 },
            imageScale: initialScale
          }, isInZone, layoutId, zoneId);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }, [element.id, updateElement, isInZone, layoutId, zoneId, baseFrameHeight, baseFrameWidth, isResponsiveWidth]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  }, [imagePosition]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // For this version, we only allow vertical movement to keep it simple,
    // or we can allow both. Let's allow both since it's "fittable".
    const newPosition = { x: newX, y: newY };

    setImagePosition(newPosition);

    // Update parent element immediately for better feedback
    updateElement(element.id, {
      imagePosition: newPosition
    }, isInZone, layoutId, zoneId);
  }, [isDragging, dragStart, element.id, updateElement, isInZone, layoutId, zoneId]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);



  // Resize functionality
  const handleResizeStart = useCallback((direction, e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    setResizeDirection(direction);
    setResizeStartData({
      mouseY: e.clientY,
      height: currentFrameHeight
    });
  }, [currentFrameHeight]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeStartData) return;

    e.preventDefault();

    const deltaY = e.clientY - resizeStartData.mouseY;

    // For full-width fittable images, only change height
    let newHeight = resizeStartData.height;

    // Calculate height change based on resize direction
    if (resizeDirection === 'se' || resizeDirection === 'sw') {
      // Bottom corners - increase height
      newHeight = Math.max(50, resizeStartData.height + deltaY);
    } else if (resizeDirection === 'ne' || resizeDirection === 'nw') {
      // Top corners - decrease height
      newHeight = Math.max(50, resizeStartData.height - deltaY);
    }

    // Apply max height limit
    newHeight = Math.min(800, newHeight);

    // Update local state for immediate visual feedback
    setCurrentFrameHeight(newHeight);

    // Don't update element during resize move to prevent re-renders
    // We'll update it on resize end instead
  }, [isResizing, resizeStartData, resizeDirection]);

  const handleResizeEnd = useCallback(() => {
    const finalHeight = currentFrameHeight;

    setIsResizing(false);
    setResizeDirection(null);
    setResizeStartData(null);

    // Update element with final height
    updateElement(element.id, {
      styles: {
        ...element.styles,
        width: isResponsiveWidth ? "100%" : element.styles?.width || 300,
        height: finalHeight
      }
    }, isInZone, layoutId, zoneId);
  }, [currentFrameHeight, element.id, element.styles, updateElement, isInZone, layoutId, zoneId, isResponsiveWidth]);

  // Global mouse events for dragging and resizing
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = (e) => handleMouseUp(e);

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      // Prevent text selection during drag
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Global mouse events for resizing
  useEffect(() => {
    if (isResizing) {
      const handleGlobalResizeMove = (e) => handleResizeMove(e);
      const handleGlobalResizeEnd = (e) => handleResizeEnd(e);

      document.addEventListener('mousemove', handleGlobalResizeMove);
      document.addEventListener('mouseup', handleGlobalResizeEnd);

      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleGlobalResizeMove);
        document.removeEventListener('mouseup', handleGlobalResizeEnd);
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const handleEdit = () => setIsEditing(true);

  const handleReset = () => {
    const resetPos = { x: 0, y: 0 };
    setImagePosition(resetPos);
    setImageScale(1);
    currentImagePos.current = resetPos;
    updateElement(element.id, {
      imagePosition: resetPos,
      imageScale: 1
    }, isInZone, layoutId, zoneId);
  };

  const renderResizeHandle = (direction, style) => (
    <Box
      sx={{
        position: 'absolute',
        width: 12,
        height: 12,
        backgroundColor: '#2630f3',
        border: '2px solid #ffffff',
        borderRadius: '50%',
        cursor: 'ns-resize',
        opacity: 0.8,
        '&:hover': {
          opacity: 1,
          transform: 'scale(1.2)'
        },
        transition: 'all 0.2s',
        zIndex: 10,
        ...style
      }}
      onMouseDown={(e) => handleResizeStart(direction, e)}
    />
  );

  // Calculate the height to use, ensuring it's always a valid number
  const calculateHeight = () => {
    if (isResizing && typeof currentFrameHeight === 'number' && currentFrameHeight > 0) {
      return Math.max(50, currentFrameHeight);
    }
    if (typeof baseFrameHeight === 'number' && baseFrameHeight > 0) {
      return Math.max(50, baseFrameHeight);
    }
    return 300; // Fallback height
  };

  const finalHeight = calculateHeight();

  return (
    <Box
      sx={{
        position: 'relative',
        width: isResponsiveWidth ? '100%' : baseFrameWidth,
        height: finalHeight, // Use number directly
        minHeight: 50, // Ensure minimum height
        ...(isResponsiveWidth ? {} : { maxWidth: `${baseFrameWidth}px` }),
        border: '2px dashed #ccc',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#f9f9f9',
        transition: isResizing ? 'none' : 'height 0.2s ease',
        cursor: isDragging ? 'grabbing' : isResizing ? 'ns-resize' : (imageSrc && !isEditing ? 'grab' : 'default'),
        userSelect: 'none',
        mx: isResponsiveWidth ? 0 : 'auto', // Center only if not full width
        '&:hover .resize-handles': {
          opacity: 1
        }
      }}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        if (onElementClick) {
          onElementClick(element, isInZone, layoutId, zoneId);
        }
      }}
      title={imageSrc && !isEditing ? "Drag top/bottom handles to resize height" : undefined}
    >
      {!imageSrc || isEditing ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            pt: 0, // Removed top padding
            px: 2,
            pb: 2
          }}
        >
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
            Fittable Image Frame
          </Typography>


          <label onClick={(e) => e.stopPropagation()}>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button variant="contained" component="span" size="small" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
              Upload Image
            </Button>
          </label>
          <Typography variant="caption" color="textSecondary" sx={{
            textAlign: 'center',
            px: 1,
            fontSize: { xs: '0.6rem', md: '0.75rem' },
            lineHeight: 1.3
          }}>
            Upload an image, then click and drag to position. Drag top/bottom handles to resize height.
          </Typography>
        </Box>
      ) : (
        <>
          <img
            ref={imageRef}
            src={imageSrc}
            alt="Fittable content"
            style={{
              position: 'absolute',
              left: '50%',
              top: 0, // Aligned to top instead of 50%
              transform: `translate(-50%, ${imagePosition.y}px) scale(${imageScale})`,
              transformOrigin: 'top center',
              maxWidth: 'none',
              maxHeight: 'none',
              pointerEvents: 'none', // Prevents image from interfering with drag
            }}
            draggable={false} // Prevents browser's native image drag
          />
        </>
      )}

      {/* Resize handles - only show when not editing */}
      {!isEditing && (
        <Box className="resize-handles" sx={{ opacity: 0, transition: 'opacity 0.2s' }}>
          {/* Top-center - for height resizing */}
          {renderResizeHandle('ne', { top: -6, left: '50%', transform: 'translateX(-50%)' })}
          {/* Bottom-center - for height resizing */}
          {renderResizeHandle('se', { bottom: -6, left: '50%', transform: 'translateX(-50%)' })}
        </Box>
      )}
    </Box>
  );
} 